import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { INQUIRY_STATUS_LABEL, INQUIRY_TYPE_LABEL } from "@shared/inquiry";
import { ORDER_STATUS_LABEL } from "@shared/orderStatus";
import { formatKRW } from "@shared/pricing";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link, useRoute } from "wouter";
import type { InquiryStatus } from "../../../../drizzle/schema";
import { AdminLayout } from "./AdminLayout";

const STATUS_COLORS: Record<InquiryStatus, string> = {
  received: "bg-yellow-100 text-yellow-800",
  answered: "bg-green-100 text-green-800",
};

function fmt(d: Date | string | null | undefined) {
  return d ? new Date(d).toLocaleString("ko-KR") : "-";
}

export function AdminInquiriesListPage() {
  const list = trpc.inquiries.adminList.useQuery();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const rows = (list.data ?? []).filter(
    (r) => statusFilter === "all" || r.status === statusFilter,
  );

  return (
    <AdminLayout>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="font-serif text-2xl text-primary">문의 관리</h1>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">상태:</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">전체</SelectItem>
              <SelectItem value="received">{INQUIRY_STATUS_LABEL.received}</SelectItem>
              <SelectItem value="answered">{INQUIRY_STATUS_LABEL.answered}</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground">{rows.length}건</div>
        </div>
      </div>

      {list.isLoading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-20 text-center text-muted-foreground">문의가 없습니다.</div>
      ) : (
        <div className="space-y-2">
          <div className="hidden lg:grid lg:grid-cols-12 gap-2 px-4 py-2 text-xs font-medium text-muted-foreground border-b border-border/60">
            <div className="col-span-2">주문번호</div>
            <div className="col-span-2">고객</div>
            <div className="col-span-1">유형</div>
            <div className="col-span-3">제목</div>
            <div className="col-span-1">상태</div>
            <div className="col-span-2">접수일</div>
            <div className="col-span-1"></div>
          </div>
          {rows.map((r) => (
            <div
              key={r.id}
              className="border border-border/60 rounded-lg hover:border-primary/30 hover:bg-primary/[0.02] transition-all px-4 py-3 grid grid-cols-2 lg:grid-cols-12 gap-2 items-center"
            >
              <div className="lg:col-span-2 text-sm font-mono">{r.orderNo}</div>
              <div className="lg:col-span-2 min-w-0">
                <div className="text-sm truncate">{r.userName ?? "미확인"}</div>
                <div className="text-xs text-muted-foreground truncate">{r.userEmail ?? ""}</div>
              </div>
              <div className="lg:col-span-1 text-xs">{INQUIRY_TYPE_LABEL[r.type]}</div>
              <div className="col-span-2 lg:col-span-3 text-sm truncate">{r.title}</div>
              <div className="lg:col-span-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[r.status]}`}>
                  {INQUIRY_STATUS_LABEL[r.status]}
                </span>
              </div>
              <div className="lg:col-span-2 text-xs text-muted-foreground">{fmt(r.createdAt)}</div>
              <div className="lg:col-span-1 text-right">
                <Button asChild size="sm" variant="outline" className="h-7 text-xs">
                  <Link href={`/admin/inquiries/${r.id}`}>보기</Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

export function AdminInquiryDetailPage() {
  const [, params] = useRoute<{ id: string }>("/admin/inquiries/:id");
  const id = Number(params?.id ?? 0);
  const utils = trpc.useUtils();
  const detail = trpc.inquiries.adminGet.useQuery({ id }, { enabled: id > 0 });
  const [answer, setAnswer] = useState("");

  const refresh = () => {
    utils.inquiries.adminGet.invalidate({ id });
    utils.inquiries.adminList.invalidate();
  };
  const saveAnswer = trpc.inquiries.adminAnswer.useMutation({
    onSuccess: () => {
      toast.success("답변을 저장했습니다. (답변완료)");
      refresh();
    },
    onError: (e) => toast.error(e.message),
  });
  const setStatus = trpc.inquiries.adminSetStatus.useMutation({
    onSuccess: () => {
      toast.success("상태를 변경했습니다.");
      refresh();
    },
    onError: (e) => toast.error(e.message),
  });

  // Pre-fill the textarea with an existing answer once it loads.
  const existing = detail.data?.inquiry.answer ?? "";
  useEffect(() => {
    setAnswer(existing);
  }, [existing]);

  if (detail.isLoading)
    return (
      <AdminLayout>
        <div className="py-20 flex justify-center">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  if (!detail.data)
    return (
      <AdminLayout>
        <div className="text-muted-foreground">문의를 찾을 수 없습니다.</div>
      </AdminLayout>
    );

  const { inquiry, order, items, customer } = detail.data;

  return (
    <AdminLayout>
      <Link
        href="/admin/inquiries"
        className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
      >
        ← 문의 목록
      </Link>
      <h1 className="font-serif text-2xl text-primary mt-2 mb-6">{inquiry.title}</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardContent className="p-5">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-3">
                <span>{INQUIRY_TYPE_LABEL[inquiry.type]}</span>
                <span>·</span>
                <span>{fmt(inquiry.createdAt)}</span>
                <span
                  className={`px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[inquiry.status]}`}
                >
                  {INQUIRY_STATUS_LABEL[inquiry.status]}
                </span>
              </div>
              <div className="text-sm whitespace-pre-wrap break-words">{inquiry.content}</div>
            </CardContent>
          </Card>

          <Card className="border-primary/20">
            <CardContent className="p-5 space-y-3">
              <div className="font-serif text-lg text-primary">관리자 답변</div>
              <Textarea
                rows={6}
                maxLength={5000}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="고객에게 보여질 답변을 입력하세요"
              />
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  disabled={!answer.trim() || saveAnswer.isPending}
                  onClick={() => saveAnswer.mutate({ id, answer: answer.trim() })}
                >
                  {saveAnswer.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  답변 저장 (답변완료 처리)
                </Button>
                {inquiry.answeredAt && (
                  <span className="text-xs text-muted-foreground">
                    마지막 답변: {fmt(inquiry.answeredAt)}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardContent className="p-5 space-y-2 text-sm">
              <div className="text-xs text-muted-foreground">상태 변경</div>
              <Select
                value={inquiry.status}
                onValueChange={(v) => setStatus.mutate({ id, status: v as InquiryStatus })}
                disabled={setStatus.isPending}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="received">{INQUIRY_STATUS_LABEL.received}</SelectItem>
                  <SelectItem value="answered">{INQUIRY_STATUS_LABEL.answered}</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 space-y-2 text-sm">
              <div className="text-xs text-muted-foreground">고객</div>
              <div>{customer?.name ?? "미확인"}</div>
              <div className="text-xs text-muted-foreground break-all">{customer?.email ?? ""}</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 space-y-2 text-sm">
              <div className="text-xs text-muted-foreground">주문 정보</div>
              <div className="font-mono">{inquiry.orderNo}</div>
              {order ? (
                <>
                  <div>{ORDER_STATUS_LABEL[order.status]}</div>
                  <div className="text-muted-foreground">{formatKRW(order.totalAmount)}</div>
                  {items.map((i) => (
                    <div key={i.id} className="text-xs text-muted-foreground">
                      {i.productName}
                    </div>
                  ))}
                  <Button asChild size="sm" variant="outline" className="mt-2">
                    <Link href={`/admin/orders/${order.id}`}>주문 관리로</Link>
                  </Button>
                </>
              ) : (
                <div className="text-xs text-muted-foreground">주문 정보를 찾을 수 없습니다.</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
